import { useState, useEffect, useMemo } from "react";
import { useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, GitMerge, CheckCircle, Phone, Mail, Building2, FileText,
  AlertTriangle, Crown, CheckSquare, Square,
} from "lucide-react";
import type { Contact } from "@shared/schema";

type Phone = { libelle: string; numero: string; principal: boolean };

function avatarInitials(prenom: string, nom: string) {
  return ((prenom?.[0] || "") + (nom?.[0] || "")).toUpperCase() || "?";
}
function avatarColor(name: string) {
  const colors = ["bg-blue-500","bg-emerald-500","bg-violet-500","bg-orange-500","bg-pink-500","bg-teal-500","bg-indigo-500","bg-amber-500"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}
function orgTypeLabel(type: string | null | undefined) {
  const map: Record<string, string> = { client: "Client", revendeur: "Revendeur", prospect: "Prospect", fournisseur: "Fournisseur", partenaire: "Partenaire", autre: "Autre" };
  return type ? (map[type] || type) : "—";
}
function normalizePhone(phone: string) {
  let c = phone.replace(/[\s.\-()]/g, "");
  if (c.startsWith("0033")) c = "0" + c.substring(4);
  if (c.startsWith("+33")) c = "0" + c.substring(3);
  return c;
}

type ContactWithCount = Contact & { organisationNom?: string; taskCount?: number };

function useSearchParam(key: string) {
  return new URLSearchParams(window.location.search).get(key);
}

function FieldRow({ label, valueA, valueB, selectedSide, onSelect }: {
  label: string;
  valueA: string | null | undefined;
  valueB: string | null | undefined;
  selectedSide: "a" | "b";
  onSelect: (side: "a" | "b") => void;
}) {
  const diff = (valueA || "") !== (valueB || "");
  return (
    <div className={`grid grid-cols-[1fr_1fr_1fr] gap-1 py-2 px-3 rounded-lg text-sm ${diff ? "bg-amber-50/50 dark:bg-amber-950/20" : ""}`}>
      <button
        type="button"
        className={`flex items-start gap-2 p-2 rounded-lg border text-left transition-all ${selectedSide === "a" ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-transparent hover:bg-muted/50"}`}
        onClick={() => onSelect("a")}
        data-testid={`field-a-${label}`}
      >
        <div className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${selectedSide === "a" ? "border-primary bg-primary" : "border-muted-foreground"}`}>
          {selectedSide === "a" && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
        </div>
        <span className={`break-all ${valueA ? "" : "text-muted-foreground italic"}`}>{valueA || "—"}</span>
      </button>

      <div className="flex items-center justify-center">
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide px-1 text-center">{label}</span>
      </div>

      <button
        type="button"
        className={`flex items-start gap-2 p-2 rounded-lg border text-left transition-all ${selectedSide === "b" ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-transparent hover:bg-muted/50"}`}
        onClick={() => onSelect("b")}
        data-testid={`field-b-${label}`}
      >
        <div className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${selectedSide === "b" ? "border-primary bg-primary" : "border-muted-foreground"}`}>
          {selectedSide === "b" && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
        </div>
        <span className={`break-all ${valueB ? "" : "text-muted-foreground italic"}`}>{valueB || "—"}</span>
      </button>
    </div>
  );
}

export default function FusionPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const idA = useSearchParam("a") || "";
  const idB = useSearchParam("b") || "";
  const candidateId = useSearchParam("cid") || null;

  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: contactA, isLoading: loadingA } = useQuery<ContactWithCount>({
    queryKey: ["/api/contacts", idA],
    queryFn: async () => {
      const [c, h] = await Promise.all([
        authFetch(`/api/contacts/${idA}`).then(r => r.json()),
        authFetch(`/api/contacts/${idA}/history`).then(r => r.json()).catch(() => []),
      ]);
      return { ...c, taskCount: h.length };
    },
    enabled: !!idA,
  });

  const { data: contactB, isLoading: loadingB } = useQuery<ContactWithCount>({
    queryKey: ["/api/contacts", idB],
    queryFn: async () => {
      const [c, h] = await Promise.all([
        authFetch(`/api/contacts/${idB}`).then(r => r.json()),
        authFetch(`/api/contacts/${idB}/history`).then(r => r.json()).catch(() => []),
      ]);
      return { ...c, taskCount: h.length };
    },
    enabled: !!idB,
  });

  const defaultMaster = useMemo(() => {
    if (!contactA || !contactB) return "a";
    const aCount = contactA.taskCount ?? 0;
    const bCount = contactB.taskCount ?? 0;
    if (aCount !== bCount) return aCount > bCount ? "a" : "b";
    const aDate = contactA.createdAt ? new Date(contactA.createdAt).getTime() : 0;
    const bDate = contactB.createdAt ? new Date(contactB.createdAt).getTime() : 0;
    return aDate <= bDate ? "a" : "b";
  }, [contactA, contactB]);

  const [master, setMaster] = useState<"a" | "b">("a");
  const [fields, setFields] = useState<Record<string, "a" | "b">>({});
  const [selectedPhones, setSelectedPhones] = useState<Record<string, boolean>>({});
  const [keepNotesA, setKeepNotesA] = useState(true);
  const [keepNotesB, setKeepNotesB] = useState(false);

  useEffect(() => {
    if (!contactA || !contactB) return;
    setMaster(defaultMaster);
    const def: Record<string, "a" | "b"> = {};
    const scalarFields = ["prenom", "nom", "email", "poste", "organisationType", "organisationId", "organisationLibre", "avatarUrl"];
    for (const f of scalarFields) {
      const va = (contactA as any)[f];
      const vb = (contactB as any)[f];
      if (!va && vb) def[f] = "b";
      else if (va && !vb) def[f] = "a";
      else def[f] = defaultMaster;
    }
    setFields(def);

    const phoneSel: Record<string, boolean> = {};
    const aPhones = (contactA.telephones as Phone[]) || [];
    const bPhones = (contactB.telephones as Phone[]) || [];
    const seen = new Set<string>();
    for (const p of aPhones) { const key = `a:${p.numero}`; phoneSel[key] = true; seen.add(normalizePhone(p.numero)); }
    for (const p of bPhones) {
      const norm = normalizePhone(p.numero);
      const key = `b:${p.numero}`;
      phoneSel[key] = !seen.has(norm);
      seen.add(norm);
    }
    setSelectedPhones(phoneSel);

    const hasNotesB = !!(contactB.notes && contactB.notes.trim());
    setKeepNotesA(true);
    setKeepNotesB(hasNotesB && (!contactA.notes || !contactA.notes.trim()));
  }, [contactA, contactB, defaultMaster]);

  const setField = (f: string, side: "a" | "b") => setFields(prev => ({ ...prev, [f]: side }));
  const togglePhone = (key: string) => setSelectedPhones(prev => ({ ...prev, [key]: !prev[key] }));

  const preview = useMemo(() => {
    if (!contactA || !contactB) return null;
    const get = (f: string) => ((fields[f] === "b" ? contactB : contactA) as any)[f] ?? null;

    const aPhones = (contactA.telephones as Phone[]) || [];
    const bPhones = (contactB.telephones as Phone[]) || [];
    const allPhones: Phone[] = [];
    const seenNorm = new Set<string>();
    for (const p of aPhones) {
      if (!selectedPhones[`a:${p.numero}`]) continue;
      const norm = normalizePhone(p.numero);
      if (!seenNorm.has(norm)) { allPhones.push(p); seenNorm.add(norm); }
    }
    for (const p of bPhones) {
      if (!selectedPhones[`b:${p.numero}`]) continue;
      const norm = normalizePhone(p.numero);
      if (!seenNorm.has(norm)) { allPhones.push(p); seenNorm.add(norm); }
    }

    let notes: string | null = null;
    const notesA = contactA.notes?.trim() || null;
    const notesB = contactB.notes?.trim() || null;
    if (keepNotesA && keepNotesB && notesA && notesB) notes = `${notesA}\n\n— Fusion —\n\n${notesB}`;
    else if (keepNotesA && notesA) notes = notesA;
    else if (keepNotesB && notesB) notes = notesB;

    return {
      prenom: get("prenom"),
      nom: get("nom"),
      email: get("email"),
      poste: get("poste"),
      telephones: allPhones,
      organisationType: get("organisationType"),
      organisationId: get("organisationId"),
      organisationLibre: get("organisationLibre"),
      avatarUrl: get("avatarUrl"),
      notes,
    };
  }, [contactA, contactB, fields, selectedPhones, keepNotesA, keepNotesB]);

  const mergeMutation = useMutation({
    mutationFn: async () => {
      const kept = master === "a" ? contactA! : contactB!;
      const deleted = master === "a" ? contactB! : contactA!;
      const fieldsMergedOrigin: Record<string, "kept" | "taken_from_deleted"> = {};
      for (const [f, side] of Object.entries(fields)) {
        fieldsMergedOrigin[f] = (side === (master === "a" ? "a" : "b")) ? "kept" : "taken_from_deleted";
      }

      const res = await authFetch("/api/contacts/merge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contactKeptId: kept.id,
          contactDeletedId: deleted.id,
          mergedFields: preview,
          fieldsMergedOrigin,
          duplicateCandidateId: candidateId,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur lors de la fusion");
      }
      return res.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/contacts"] });
      qc.invalidateQueries({ queryKey: ["/api/contacts/duplicates"] });
      qc.invalidateQueries({ queryKey: ["/api/contacts/duplicates/count"] });
      toast({ title: "Fusion effectuée", description: `${data.tachesRelinked} tâche${data.tachesRelinked !== 1 ? "s" : ""} reroutée${data.tachesRelinked !== 1 ? "s" : ""}` });
      navigate(`/contacts/${data.keptContactId}`);
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const loading = loadingA || loadingB;
  const cA = contactA;
  const cB = contactB;

  if (!idA || !idB) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-muted-foreground">Paramètres manquants. Revenez à l'annuaire.</p>
        <Link href="/contacts"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Annuaire</Button></Link>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Chargement…</p></div>;
  if (!cA || !cB) return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Un contact est introuvable.</p></div>;

  const kept = master === "a" ? cA : cB;
  const deleted = master === "a" ? cB : cA;
  const aPhones = (cA.telephones as Phone[]) || [];
  const bPhones = (cB.telephones as Phone[]) || [];

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center gap-3">
        {candidateId ? (
          <Link href="/contacts/doublons">
            <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Doublons</Button>
          </Link>
        ) : (
          <Link href="/contacts">
            <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Annuaire</Button>
          </Link>
        )}
      </div>

      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <GitMerge className="h-6 w-6 text-primary" />
          Fusionner deux contacts
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Comparez les fiches et choisissez les valeurs à conserver</p>
      </div>

      {/* Sélection du maître */}
      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
        {[{ contact: cA, side: "a" as const }, { contact: cB, side: "b" as const }].map(({ contact, side }) => {
          const isMaster = master === side;
          return (
            <button
              key={side}
              type="button"
              className={`rounded-xl border p-4 text-left transition-all ${isMaster ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border bg-card hover:bg-muted/40"}`}
              onClick={() => setMaster(side)}
              data-testid={`btn-master-${side}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Avatar className="h-10 w-10">
                    {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} />}
                    <AvatarFallback className={`text-sm font-bold text-white ${avatarColor(contact.prenom + contact.nom)}`}>
                      {avatarInitials(contact.prenom, contact.nom)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-sm">{contact.prenom} {contact.nom}</p>
                    {contact.poste && <p className="text-xs text-muted-foreground">{contact.poste}</p>}
                  </div>
                </div>
                {isMaster && <Crown className="h-4 w-4 text-amber-500 shrink-0 mt-1" title="Fiche conservée" />}
              </div>
              <div className="flex flex-wrap gap-1">
                <Badge variant={isMaster ? "default" : "outline"} className="text-[10px] h-4 px-1.5">
                  {isMaster ? "✓ Conserver" : "Supprimer"}
                </Badge>
                {(contact.taskCount ?? 0) > 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                    🔗 {contact.taskCount} tâche{(contact.taskCount ?? 0) > 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
            </button>
          );
        })}
        <div className="flex items-center justify-center text-xs text-muted-foreground font-medium">VS</div>
      </div>

      {/* Sélection champ par champ */}
      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_1fr] bg-secondary/40 border-b px-3 py-2 text-xs font-semibold text-muted-foreground">
          <div className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-primary" /> Contact A</div>
          <div className="text-center">Champ</div>
          <div className="flex items-center justify-end gap-1">Contact B <CheckCircle className="h-3 w-3 text-muted-foreground" /></div>
        </div>
        <div className="p-2 space-y-0.5">
          <FieldRow label="Prénom" valueA={cA.prenom} valueB={cB.prenom} selectedSide={fields.prenom || "a"} onSelect={s => setField("prenom", s)} />
          <FieldRow label="Nom" valueA={cA.nom} valueB={cB.nom} selectedSide={fields.nom || "a"} onSelect={s => setField("nom", s)} />
          <FieldRow label="Poste" valueA={cA.poste} valueB={cB.poste} selectedSide={fields.poste || "a"} onSelect={s => setField("poste", s)} />
          <FieldRow label="Email" valueA={cA.email} valueB={cB.email} selectedSide={fields.email || "a"} onSelect={s => setField("email", s)} />
          <FieldRow
            label="Organisation"
            valueA={cA.organisationNom || cA.organisationLibre || (cA.organisationType ? orgTypeLabel(cA.organisationType) : null)}
            valueB={cB.organisationNom || cB.organisationLibre || (cB.organisationType ? orgTypeLabel(cB.organisationType) : null)}
            selectedSide={fields.organisationType || "a"}
            onSelect={s => { setField("organisationType", s); setField("organisationId", s); setField("organisationLibre", s); }}
          />
        </div>
      </div>

      {/* Téléphones */}
      {(aPhones.length > 0 || bPhones.length > 0) && (
        <div className="rounded-xl border bg-card shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Téléphones</h3>
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5">Sélection multiple</Badge>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[...aPhones.map(p => ({ ...p, side: "a" as const })), ...bPhones.map(p => ({ ...p, side: "b" as const }))].map(p => {
              const key = `${p.side}:${p.numero}`;
              const checked = !!selectedPhones[key];
              return (
                <button
                  key={key}
                  type="button"
                  className={`flex items-center gap-2.5 p-2.5 rounded-lg border text-sm transition-all text-left ${checked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}
                  onClick={() => togglePhone(key)}
                  data-testid={`phone-toggle-${key}`}
                >
                  {checked ? <CheckSquare className="h-4 w-4 text-primary shrink-0" /> : <Square className="h-4 w-4 text-muted-foreground shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-medium truncate">{p.numero}</p>
                    <p className="text-xs text-muted-foreground">{p.libelle} · Contact {p.side.toUpperCase()}{p.principal ? " · Principal" : ""}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes */}
      {(cA.notes || cB.notes) && (
        <div className="rounded-xl border bg-card shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Notes</h3>
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5">Sélection multiple</Badge>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[{ side: "a", notes: cA.notes, state: keepNotesA, toggle: () => setKeepNotesA(p => !p) }, { side: "b", notes: cB.notes, state: keepNotesB, toggle: () => setKeepNotesB(p => !p) }]
              .filter(n => n.notes)
              .map(n => (
                <button
                  key={n.side}
                  type="button"
                  className={`flex items-start gap-2.5 p-2.5 rounded-lg border text-sm text-left transition-all ${n.state ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}
                  onClick={n.toggle}
                >
                  {n.state ? <CheckSquare className="h-4 w-4 text-primary shrink-0 mt-0.5" /> : <Square className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Notes du contact {n.side.toUpperCase()}</p>
                    <p className="text-xs line-clamp-3">{n.notes}</p>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Aperçu résultat */}
      {preview && (
        <div className="rounded-xl border bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900 p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">Aperçu du contact fusionné</h3>
          </div>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              {preview.avatarUrl && <AvatarImage src={preview.avatarUrl} />}
              <AvatarFallback className={`text-sm font-bold text-white ${avatarColor((preview.prenom || "") + (preview.nom || ""))}`}>
                {avatarInitials(preview.prenom || "", preview.nom || "")}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-0.5">
              <p className="font-semibold">{preview.prenom} {preview.nom}</p>
              {preview.poste && <p className="text-xs text-muted-foreground">{preview.poste}</p>}
              {preview.email && <p className="text-xs flex items-center gap-1 text-muted-foreground"><Mail className="h-3 w-3" />{preview.email}</p>}
              {preview.telephones.length > 0 && <p className="text-xs flex items-center gap-1 text-muted-foreground"><Phone className="h-3 w-3" />{preview.telephones.map(t => t.numero).join(", ")}</p>}
              {(preview.organisationNom || preview.organisationLibre) && (
                <p className="text-xs flex items-center gap-1 text-muted-foreground"><Building2 className="h-3 w-3" />{preview.organisationNom || preview.organisationLibre}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Avertissement + boutons */}
      <div className="rounded-xl border bg-amber-50/70 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 p-4">
        <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">La fiche de <span className="underline">{deleted.prenom} {deleted.nom}</span> sera supprimée définitivement après fusion.</p>
            <p className="text-xs mt-0.5 text-amber-700 dark:text-amber-400">
              Les {deleted.taskCount ?? 0} tâche{(deleted.taskCount ?? 0) !== 1 ? "s" : ""} qui lui sont rattachée{(deleted.taskCount ?? 0) !== 1 ? "s" : ""} seront reroutée{(deleted.taskCount ?? 0) !== 1 ? "s" : ""} sur {kept.prenom} {kept.nom}.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pb-6">
        <Link href={candidateId ? "/contacts/doublons" : "/contacts"}>
          <Button variant="outline">Annuler</Button>
        </Link>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={mergeMutation.isPending}
          data-testid="btn-confirm-merge"
        >
          <GitMerge className="h-4 w-4 mr-2" />
          {mergeMutation.isPending ? "Fusion en cours…" : "Fusionner les contacts"}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent onCloseAutoFocus={e => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la fusion ?</AlertDialogTitle>
            <AlertDialogDescription>
              La fiche de <strong>{deleted.prenom} {deleted.nom}</strong> sera supprimée définitivement et ses tâches reroutées vers <strong>{kept.prenom} {kept.nom}</strong>. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary hover:bg-primary/90"
              onClick={() => { setConfirmOpen(false); mergeMutation.mutate(); }}
              data-testid="btn-final-confirm-merge"
            >
              Fusionner
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
